import { WisdomCard } from '@components/Cards';
import { CarouselLeftArrowIcon } from '@components/Icons/CarouselLeftArrowIcon';
import { CarouselRightArrowIcon } from '@components/Icons/CarouselRightArrowIcon';
import NapsterSpinner from '@components/NapsterSpinner';
import { useCommonState } from '@hooks/storeHooks/useCommonState';
import { useMusicState } from '@hooks/storeHooks/useMusicState';
import useAlbumData from '@hooks/useAlbumData';
import { useStationMode } from '@hooks/station';
import { useFavorites } from '@hooks/useFavorites';
import { useSwipeCarousel } from '@hooks/useSwipeCarousel';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { placeHolderData } from '../../../../config';
import { Album } from '../../../../types';
import { getTrackUrlForLocale } from '../../../../utils/localeUtils';

interface HomeWisdomAlbumGridProps {
  storeKey: 'musicforyouList' | 'serenityPlaylist' | 'wisdomPlaylist';
  category?: string;
  gridId: string;
  playlistIdPrefix: string;
  albumCount?: number;
  randomize?: boolean;
  selectionMode?: 'random' | 'first' | 'audiobooks';
  albums?: Album[]; // Optional: pass albums directly (used for home page with filtered items)
}

export const HomeWisdomAlbumGrid = ({
  storeKey,
  category,
  gridId,
  playlistIdPrefix,
  albumCount = 4,
  randomize = true,
  selectionMode = 'random',
  albums: passedAlbums,
}: HomeWisdomAlbumGridProps) => {
  const { t } = useTranslation();
  const {
    musicforyouList,
    serenityPlaylist,
    wisdomPlaylist,
    setActivePlaylist,
    addRecentTrack,
    preferredLocale,
  } = useMusicState();
  const { setActivePopup } = useCommonState();
  const { toggleFavorite } = useFavorites();
  const { loadAlbumById } = useAlbumData();
  const isStationMode = useStationMode();
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [loadingAlbumId, setLoadingAlbumId] = useState<string | null>(null);
  const carouselRef = useRef<HTMLDivElement>(null);

  // Add swipe functionality
  const { containerRef } = useSwipeCarousel({
    threshold: 50,
  });

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    const scrollLeft = container.scrollLeft;
    const maxScroll = container.scrollWidth - container.clientWidth;

    setCanScrollLeft(scrollLeft > 25);
    setCanScrollRight(scrollLeft < maxScroll - 25);
  };
  // Get albums from the specified store or use passed albums (for home page)
  const albums = useMemo(() => {
    // If albums are passed directly (home page with filtered items), use them
    if (passedAlbums && passedAlbums.length > 0) {
      return passedAlbums;
    }

    let sourceAlbums: Album[] = [];

    switch (storeKey) {
      case 'musicforyouList':
        sourceAlbums = musicforyouList;
        // Only filter by category for musicforyouList since it contains mixed categories
        if (category) {
          sourceAlbums = sourceAlbums.filter(
            (album) => album.category === category,
          );
        }
        break;
      case 'serenityPlaylist':
        sourceAlbums = serenityPlaylist;
        // serenityPlaylist should already contain only serenity albums
        break;
      case 'wisdomPlaylist':
        sourceAlbums = wisdomPlaylist;
        // wisdomPlaylist should already contain only audiobooks/book albums
        break;
    }

    return sourceAlbums;
  }, [
    storeKey,
    musicforyouList,
    serenityPlaylist,
    wisdomPlaylist,
    category,
    passedAlbums,
  ]);
  // Select albums based on selection mode - ensuring no duplicate album names
  const selectedAlbums = useMemo(() => {
    if (!albums) return [];

    // If albums are passed directly (home page with filtered items), show all of them
    if (passedAlbums && passedAlbums.length > 0) {
      return albums;
    }

    // Filter out albums with duplicate names first
    // const uniqueAlbums = albums.filter(
    //   (album, index, arr) =>
    //     arr.findIndex((a) => a.albumName === album.albumName) === index,
    // );
    const uniqueAlbums = albums;

    // Apply selection logic based on mode
    if (selectionMode === 'audiobooks') {
      // Audiobook selection logic:
      // If more than 5 items: skip first 2 and take next 4 (indices 2,3,4,5)
      // If 5 or fewer items: take first 4
      if (uniqueAlbums.length > 5) {
        return uniqueAlbums.slice(2, 6); // Skip first 2, take next 4 (indices 2,3,4,5)
      } else {
        return uniqueAlbums.slice(0, Math.min(albumCount, uniqueAlbums.length));
      }
    } else if (randomize || selectionMode === 'random') {
      // Fisher-Yates shuffle algorithm for better randomization
      const shuffled = [...uniqueAlbums];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled.slice(0, Math.min(albumCount, uniqueAlbums.length));
    } else {
      // For 'first' mode or non-random, take first N
      return uniqueAlbums.slice(0, Math.min(albumCount, uniqueAlbums.length));
    }
  }, [albums, albumCount, randomize, selectionMode, passedAlbums]);

  const playAlbum = useCallback(
    async (album: Album) => {
      try {
        setLoadingAlbumId(album.id);
        // Fetch complete album data from API
        const albumData = await loadAlbumById(album.id);

        if (!albumData || !albumData.tracks || albumData.tracks.length === 0) {
          console.warn(
            'No tracks found in album:',
            albumData?.albumName || album.id,
          );
          setLoadingAlbumId(null);
          return;
        }

        // Get the first contributor for artist info
        const contributor =
          albumData.contributors && albumData.contributors.length > 0
            ? albumData.contributors[0]
            : null;
        const firstTrack = albumData.tracks[0];

        const playlist = {
          currentIndex: 0,
          album: albumData,
        };

        // Add first track to recent tracks when album is played
        const recentTrack = {
          track: {
            name: firstTrack.name,
            url: getTrackUrlForLocale(firstTrack, preferredLocale),
            trackIndex: 1,
          },
          contributor: {
            id: albumData.id,
            givenName: contributor?.givenName || 'Unknown Artist',
            thumbnailUrl:
              contributor?.thumbnailUrl || placeHolderData.contributorThumbnail,
          },
          albumArtUrl:
            albumData.albumArtUrl ||
            contributor?.thumbnailUrl ||
            placeHolderData.albumCover,
          timestamp: Date.now(),
        };
        addRecentTrack(recentTrack);

        setActivePlaylist(playlist);
        setActivePopup('musicplayer');
        setLoadingAlbumId(null);
      } catch (error) {
        console.error('Error loading album:', error);
        setLoadingAlbumId(null);
      }
    },
    [
      loadAlbumById,
      setActivePlaylist,
      setActivePopup,
      playlistIdPrefix,
      addRecentTrack,
      preferredLocale,
    ],
  );

  return (
    <div id={gridId} className="relative w-full group">
      {/* Left Arrow */}
      {canScrollLeft && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const container = containerRef.current || carouselRef.current;
            if (container) {
              container.scrollBy({
                left: -400,
                behavior: 'smooth',
              });
            }
          }}
          className="absolute left-9 top-1/2 -translate-y-1/2 z-[300] hidden sm:flex items-center justify-center w-12 h-12 rounded-full bg-black/60 hover:bg-black/80 transition-all duration-200 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto"
          aria-label={t('aria.previousSlide')}>
          <CarouselLeftArrowIcon />
        </button>
      )}

      {/* Right Arrow */}
      {canScrollRight && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const container = containerRef.current || carouselRef.current;
            if (container) {
              container.scrollBy({
                left: 400,
                behavior: 'smooth',
              });
            }
          }}
          className="absolute right-9 top-1/2 -translate-y-1/2 z-[300] hidden sm:flex items-center justify-center w-12 h-12 rounded-full bg-black/60 hover:bg-black/80 transition-all duration-200 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto"
          aria-label={t('aria.nextSlide')}>
          <CarouselRightArrowIcon />
        </button>
      )}

      <div
        ref={(el) => {
          carouselRef.current = el;
          containerRef.current = el;
        }}
        className="flex gap-[16px] pb-[30px] overflow-x-auto snap-x snap-mandatory scroll-smooth hide-scrollbar scrollbar-hide select-none scroll-pl-4 sm:scroll-pl-6"
        onScroll={handleScroll}>
        {/* Initial spacer */}
        <div className="flex-shrink-0 w-4 sm:w-6"></div>
        {selectedAlbums?.map((album: Album, index: number) => {
          const contributor = album.contributors[0];
          const isLoading = loadingAlbumId === album.id;
          return (
            <div
              key={`${playlistIdPrefix}-${index}`}
              className={`flex-shrink-0 snap-start relative ${
                isStationMode ? 'w-[282px]' : 'w-[371px]'
              }`}>
              <WisdomCard
                album={album}
                contributor={contributor}
                albumArtUrl={album.albumArtUrl}
                albumName={album.albumName}
                likes={album.likes}
                isLiked={album.isLiked}
                onClick={() => playAlbum(album)}
                onFavoriteClick={(e) => {
                  e.stopPropagation();
                  toggleFavorite(
                    'album',
                    album.id,
                    album.isLiked,
                    album.albumName,
                    contributor?.givenName,
                    album.category,
                  );
                }}
                showLikesCount={true}
                compactSize={isStationMode}
              />
              {isLoading && (
                <div className="absolute z-[100] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                  <NapsterSpinner />
                </div>
              )}
            </div>
          );
        })}
        {/* Final spacer */}
        <div className="flex-shrink-0 w-4 sm:w-6"></div>
      </div>
    </div>
  );
};
